import { Stack, TextField, Theme } from '@mui/material';
import {
  ChangeEvent,
  FocusEvent,
  FocusEventHandler,
  KeyboardEvent,
  ReactNode,
  forwardRef,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { FormProps } from '../helpers';
import { createUseStyles } from 'react-jss';
import Icon from '../../icon';
import CopyClipButton from '@/components/copy-clip-button';
import { openNewTab, addProtocol } from '@/lib/url-helpers';
import Text from '@/components/text';
import { IconType } from '@/components/icon/helpers';

interface Props extends FormProps {
  value: string;
  onChange?: (value: string, event?: ChangeEvent<HTMLInputElement>) => void;
  onEnter?: (value: string) => void;
  type?: string;
  autoComplete?: string;
  rows?: number;
  size?: 'x-small' | 'small' | 'medium';
  placeholder?: string;
  clearable?: boolean;
  startIcon?: IconType;
  copyClip?: boolean;
  openUrl?: boolean;
  readonly?: boolean;
  maxLength?: number;
  textPrefix?: string;
  updateOnly?: boolean;
  length?: number;
  hint?: string;
}

const useStyles = createUseStyles<
  string,
  { isTextarea: boolean; size: string; updateOnly: boolean }
>((theme: Theme) => ({
  textfield: ({ isTextarea, size, updateOnly }) => ({
    backgroundColor: theme.palette.common.white,
    borderRadius: theme.shape.borderRadius,
    '& label.MuiFormLabel-root.Mui-focused': {
      color: theme.palette.common.black,
    },
    '& .MuiInputBase-root': {
      cursor: updateOnly ? 'pointer' : 'default',
      paddingRight: isTextarea ? 0 : 6,
      '&.Mui-focused': {
        '& fieldset': {
          borderColor: theme.palette.gray,
        },
      },
      '&.Mui-readOnly fieldset': {
        borderColor: `${theme.palette.divider} !important`,
        backgroundColor: '#e8e8e830',
      },
      '&.MuiInputBase-adornedStart': {
        paddingLeft: 6,
        whiteSpace: 'nowrap',
      },
      '& input.MuiInputBase-input': {
        fontSize: size === 'x-small' ? '14px' : 'inherit',
        pointerEvents: updateOnly ? 'none' : 'auto',
      },
      '& input.MuiInputBase-inputAdornedEnd': {
        paddingRight: 12,
      },
      '& input.Mui-readOnly, textarea.Mui-readOnly': {
        color: `${theme.palette.darkGray} !important`,
      },
    },
    '& .MuiFormHelperText-root': {
      marginLeft: 0,
    },
    '& .update-only-unlock-icon': {
      display: 'none',
    },
    '&:hover': {
      '& .update-only-unlock-icon': {
        display: 'block',
      },
      '& .update-only-lock-icon': {
        display: 'none',
      },
    },
    ...(isTextarea && {
      '& textarea': {
        paddingRight: 14,
      },
    }),
  }),
  startAdornment: {
    marginRight: 2,
  },
  notchedOutline: {
    borderWidth: '1px !important',
  },
}));

const TextInput = forwardRef<HTMLInputElement, Props>(
  (
    {
      name,
      label = '',
      onChange,
      onEnter,
      onBlur,
      type = 'text',
      autoComplete = '',
      autoFocus = false,
      value,
      error = '',
      disabled = false,
      required = false,
      rows,
      size = 'medium',
      placeholder,
      clearable = false,
      copyClip = false,
      openUrl = false,
      startIcon,
      readonly = false,
      maxLength,
      textPrefix = '',
      updateOnly: propUpdateOnly = false,
      fullWidth = true,
      length = 16,
      hint = '',
    },
    ref
  ) => {
    const isSmall = ['x-small', 'small'].includes(size);
    const isTextarea = type === 'textarea';
    const [updateOnly, setUpdateOnly] = useState(propUpdateOnly);

    const styles = useStyles({ isTextarea, size, updateOnly });

    const inputRef = useRef<HTMLInputElement>(null);

    const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
      if (readonly) {
        return;
      }
      onChange?.(String(event.target.value), event);
    };

    const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'Enter') {
        onEnter?.(value);
      }
    };

    const handleBlur: FocusEventHandler<HTMLInputElement | HTMLTextAreaElement> = (event) => {
      let value = String(event.target.value);
      if (type === 'url') {
        value = addProtocol(value);
        onChange?.(value, event as FocusEvent<HTMLInputElement>);
      }
      onBlur?.(value, event as FocusEvent<HTMLInputElement>);
    };

    const TextareaProps = {
      multiline: false,
      minRows: 5,
      maxRows: 5,
    };

    if (isTextarea) {
      TextareaProps.multiline = true;
      if (rows) {
        TextareaProps.minRows = rows;
        TextareaProps.maxRows = rows;
      }
    }

    const handleClear = () => {
      if (readonly) {
        return;
      }
      onChange?.('');
    };

    const handleOpenUrl = () => {
      openNewTab(value);
    };

    const handleUnlockUpdate = () => {
      setUpdateOnly(false);
      onChange?.('');
      inputRef.current?.focus();
    };

    let EndAdornmentEl: ReactNode | null = null;
    const endAdornmentEls: ReactNode[] = [];
    const showClearIcon = clearable && Boolean(value);

    if (showClearIcon) {
      endAdornmentEls.push(<Icon name="close" onClick={handleClear} key="clear" />);
    }

    if (openUrl) {
      endAdornmentEls.push(
        <Icon name="open-in-new" onClick={handleOpenUrl} color="link" key="link" />
      );
    }

    if (copyClip) {
      endAdornmentEls.push(<CopyClipButton value={value || ''} key="copy" />);
    }

    if (updateOnly) {
      endAdornmentEls.push(
        <Icon
          name="lock"
          onClick={handleUnlockUpdate}
          key="lock"
          className="update-only-lock-icon"
        />
      );
      endAdornmentEls.push(
        <Icon
          name="unlock"
          onClick={handleUnlockUpdate}
          key="unlock"
          className="update-only-unlock-icon"
        />
      );
    }

    if (endAdornmentEls.length) {
      EndAdornmentEl = (
        <Stack direction="row" alignItems="center" gap={1}>
          {endAdornmentEls}
        </Stack>
      );
    }

    let StartAdornment: ReactNode | null = null;
    if (startIcon) {
      StartAdornment = <Icon className={styles.startAdornment} name="search" />;
    }

    if (textPrefix) {
      StartAdornment = <Text color="grey">{textPrefix}</Text>;
    }

    const valueLabel = useMemo(() => {
      if (updateOnly) {
        return '*'.repeat(length - hint.length) + hint;
      }
      return value || '';
    }, [updateOnly, length, hint, value]);

    // make sure autofocus actually works
    // https://stackoverflow.com/questions/60476155/is-it-safe-to-use-ref-current-as-useeffects-dependency-when-ref-points-to-a-dom
    useEffect(() => {
      if (!autoFocus) {
        return;
      }
      setTimeout(() => {
        inputRef.current?.focus();
      }, 300);
    }, [autoFocus]);

    // controlled updateOnly value
    useEffect(() => {
      setUpdateOnly(propUpdateOnly);
    }, [propUpdateOnly, hint]);

    return (
      <TextField
        id={name}
        name={name}
        ref={ref}
        label={label || undefined}
        variant="outlined"
        onClick={updateOnly ? handleUnlockUpdate : undefined}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        value={valueLabel}
        margin={isSmall ? 'none' : 'normal'}
        fullWidth={fullWidth}
        type={type}
        autoComplete={autoComplete}
        autoFocus={autoFocus}
        error={!!error}
        helperText={error}
        disabled={disabled}
        {...TextareaProps}
        className={styles.textfield}
        required={required}
        size={isSmall ? 'small' : 'medium'}
        placeholder={placeholder}
        inputRef={inputRef}
        inputProps={{ maxLength }}
        InputProps={{
          classes: {
            notchedOutline: styles.notchedOutline,
          },
          startAdornment: StartAdornment,
          endAdornment: EndAdornmentEl,
          readOnly: updateOnly || readonly,
        }}
      />
    );
  }
);

TextInput.displayName = 'TextInput';

export default TextInput;
